import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import "./RunQueue.css";
import RunQueuePanel from "./RunQueuePanel";
import { canSeeMultiProjectRuns, canSeeWholeQueue, visibleProjectIds } from "./runQueueAccess";
import { runDashboardHref } from "../../../utils/rsaRouteParams";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { useRunQueue } from "../../../hooks/useRunQueue";
import { useProject } from "../../../contexts/ProjectContext";
import { useFileContext } from "../../../ContextAPI/FileContext";

export const DEFAULT_HISTORY = { since: "today", mine: false };

/**
 * "Multi-project runs" — the left-sidebar view (2026-09-08; picker removed the
 * same evening: runs enter the queue from a project's own form). Wraps the
 * RunQueuePanel full-width and decides its scope:
 *
 *   admin (view_all_projects)  → every entry, fleet board, may reorder /
 *                                cancel / start held entries
 *   user / super user          → only entries of the projects they can open
 *                                (the Projects list: ProjectContext, fed by
 *                                /api/user-projects), read-only, no fleet board
 *
 * The scoping is client-enforced (project_ids on the queue GETs) — the
 * Pipeline API has no user identity. See runQueueAccess.js.
 *
 * History filters live here: `since` (today | week | all) and `mine` (the
 * signed-in user's email) go to useRunQueue → GET /rsa/v2/queue.
 */
function MultiProjectRunsPage() {
  const { permissions, loading: permsLoading } = useUserPermissions();
  const { userEmail } = useFileContext();
  const {
    projects: myProjects,
    loading: myProjectsLoading,
    lastUpdated: myProjectsLoadedAt,
    loadProjects,
  } = useProject();
  const isAdmin = canSeeWholeQueue(permissions);

  // The user's own project list (what /projects shows). Cached in the
  // app-wide ProjectProvider, so this is a no-op after the first visit.
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const myProjectIds = useMemo(() => visibleProjectIds(myProjects), [myProjects]);
  const myProjectsReady = !myProjectsLoading && (myProjectsLoadedAt != null || myProjectIds.length > 0);
  // Never poll unscoped for a non-admin: wait for the project list, and if it
  // is empty there is nothing to show (an empty project_ids would mean "all").
  const scopeReady =
    !permsLoading &&
    (isAdmin || (myProjectsReady && myProjectIds.length > 0 && canSeeMultiProjectRuns(permissions, myProjects)));
  const projectIds = isAdmin ? null : myProjectIds;

  const email = userEmail || (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";
  const [history, setHistory] = useState(DEFAULT_HISTORY);

  // Row name → the run's dashboard: /projects/<id>?run=<run_id> opens the
  // project's RSA Dashboard tab with that tile selected (deep link, 2026-09-08).
  const navigate = useNavigate();
  const openRun = useCallback(
    (entry) => {
      const href = runDashboardHref(entry?.project_id, entry?.run_id);
      if (href) navigate(href);
    },
    [navigate],
  );

  const runQueue = useRunQueue({
    enabled: scopeReady,
    pollMs: 60000,
    projectIds,
    since: history.since,
    mine: history.mine ? email || null : null,
  });

  if (permsLoading) {
    return (
      <div className="multi-project-runs-page" data-testid="multi-project-runs-page">
        <div className="run-queue-state">Loading…</div>
      </div>
    );
  }
  // Direct URL without the sidebar entry: one RSA project (or none) has no
  // multi-project view — back to Projects.
  if (!isAdmin && myProjectsReady && myProjectIds.length > 0 && !canSeeMultiProjectRuns(permissions, myProjects)) {
    return <Navigate to="/projects" replace />;
  }
  if (!isAdmin && myProjectsReady && myProjectIds.length === 0) {
    return (
      <div className="multi-project-runs-page" data-testid="multi-project-runs-page">
        <div className="run-queue-state" data-testid="multi-project-runs-empty">
          You are not assigned to any project yet, so there are no runs to show here.
        </div>
      </div>
    );
  }
  return (
    <div className="multi-project-runs-page" data-testid="multi-project-runs-page">
      <RunQueuePanel
        userEmail={email}
        isAdmin={isAdmin}
        runQueue={runQueue}
        history={history}
        onHistoryChange={setHistory}
        onOpenRun={openRun}
      />
    </div>
  );
}

export default MultiProjectRunsPage;
